import t from '../i18n'
import DataMgr from './DataMgr'

export default class Utils {
  static htmlEntities (s: string): string {
    return s.replace(/[\u00A0-\u9999<>&]/g, function (i) {
      return '&#' + i.charCodeAt(0) + ';'
    })
  }

  static clamp (n: number, mn: number, mx: number): number {
    return Math.min(Math.max(n, mn), mx)
  }

  static dateAge (src: Date, ref: Date): string {
    const diff = (src.getTime() - ref.getTime())
    const absDiff = Math.abs(diff) / 1000.0
    const rtf1 = new Intl.RelativeTimeFormat()
    const options: Array<{cutoff: number; unit: Intl.RelativeTimeFormatUnit}> = [
      { cutoff: 3600 * 24 * 365, unit: 'year' },
      { cutoff: 3600 * 24 * 30, unit: 'month' },
      { cutoff: 3600 * 24 * 7, unit: 'week' },
      { cutoff: 3600 * 24, unit: 'day' },
      { cutoff: 3600, unit: 'hour' },
      { cutoff: 60, unit: 'minute' },
      { cutoff: 1, unit: 'second' }
    ]
    for (const opt of options) {
      if (absDiff > opt.cutoff) {
        return rtf1.format(Math.round(diff / (opt.cutoff * 1000)), opt.unit)
      }
    }
    return t('just now')
  }

  static renderDuration (durationString: string): string {
    const matches = durationString.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/)
    if (matches == null) return durationString
    const parts = [
      { unit: 'year', toHours: 8766, value: matches[1] },
      { unit: 'month', toHours: 730.5, value: matches[2] },
      { unit: 'week', toHours: 168, value: matches[3] },
      { unit: 'day', toHours: 24, value: matches[4] },
      { unit: 'hour', toHours: 1, value: matches[5] },
      { unit: 'minute', toHours: 1 / 60, value: matches[6] },
      { unit: 'second', toHours: 1 / 3600, value: matches[7] }
    ]
    return (parseInt(matches[0]) > 0 || parseInt(matches[1]) > 0 ? 'Approximately ' : '') + parts
      .filter((part) => { return part.value !== undefined })
      .reduce((total, part) => { return total + parseInt(part.value) * part.toHours }, 0)
  }

  // Convert a string into a 32-bit number.
  static hashCode (input: string): number {
    let hash = 0; let i; let chr
    if (input.length === 0) return hash
    for (i = 0; i < input.length; i++) {
      chr = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + chr
      hash |= 0 // Convert to 32bit integer
    }
    return hash
  }

  static serializeJson (fe: HTMLFormElement, initial?: Record<string, string|number|number[]>): string {
    const ret: Record<string, string|number|number[]> = initial || {}
    const fd = new FormData(fe)
    fd.forEach((val, key) => {
      if (key !== undefined) {
        ret[key] = (val === undefined ? '' : (val as string))
      }
    })
    return JSON.stringify(ret)
  }

  static dateCompare (a: string | Date, b: string | Date): 0 | 1 | -1 {
    const ma = !a || typeof a === 'string' ? new Date(a) : a
    const mb = !b || typeof b === 'string' ? new Date(b) : b
    if (ma.getTime() > mb.getTime()) return 1
    if (ma.getTime() < mb.getTime()) return -1
    return 0
  }

  static rssiToSignalNumber (rssi: number | null): number {
    const signalStrength = rssi === null ? null : Math.abs(rssi)
    if (signalStrength == null) {
      // no signal
      return 0
    } else if (signalStrength >= 10 && signalStrength <= 59) {
      return 5
    } else if (signalStrength >= 60 && signalStrength <= 79) {
      return 4
    } else if (signalStrength >= 80 && signalStrength <= 99) {
      return 3
    } else if (signalStrength >= 100 && signalStrength <= 109) {
      return 2
    } else {
      return 1
    }
  }

  static timeToPositionAge (latestTime: string): number {
    const fourHoursInMinutes = 240
    const twentyFourHoursInMinutes = 1440
    const sevenDaysInMinutes = twentyFourHoursInMinutes * 7
    if (latestTime !== '') {
      const positionTime = Date.parse(latestTime)
      const positionAgeMinutes = (Date.now() - positionTime) / 60000

      // calculate icon values
      if (positionAgeMinutes <= fourHoursInMinutes) {
        return 1
      } else if ((positionAgeMinutes > fourHoursInMinutes && positionAgeMinutes <= twentyFourHoursInMinutes)) {
        return 2
      } else if (positionAgeMinutes > twentyFourHoursInMinutes && positionAgeMinutes <= sevenDaysInMinutes) {
        return 3
      }
    }
    return 4
  }

  static numbers = '0123456789'
  static upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  static lowerCase = 'abcdefghijklmnopqrstuvwxyz'
  static special = "=+-^$*.[]{}()?\"!@#%&/\\,><':;|_~`"
  static generatePassword (): string {
    const vals = [Utils.numbers, Utils.upperCase, Utils.lowerCase, Utils.special]
    const numEach = vals.map(() => 0)
    const len = 8
    const maxNumEach = Math.ceil(len / vals.length)
    let ret = ''
    while (!Utils.validatePassword(ret)) {
      let srcIdx = Math.floor(Math.random() * vals.length)
      while (numEach[srcIdx] > maxNumEach) {
        srcIdx = Math.floor(Math.random() * vals.length)
      }
      numEach[srcIdx] += 1
      const src = vals[srcIdx]
      const startIdx = Math.floor(Math.random() * src.length)
      ret += src.slice(startIdx, startIdx + 1)
    }
    return ret
  }

  static validatePassword (pw: string): boolean {
    if (pw.length < 8 || pw.length > 99) return false
    const vals = [Utils.numbers, Utils.upperCase, Utils.lowerCase, Utils.special]
    const numEach = vals.map(() => 0)
    for (let idx = 0; idx < pw.length; ++idx) {
      const ch = pw.charAt(idx)
      for (const valIdx in vals) {
        const valSrc = vals[valIdx]
        if (valSrc.indexOf(ch) >= 0) {
          numEach[valIdx] += 1
        }
      }
    }
    const isValid = numEach.every((x) => x >= 1)
    return isValid
  }

  static resourceString (otherIds?: { tokara?: string | Array<string>, alldayrtk?: string | Array<string>} | Record<string, string | Array<string> | null> | null): string {
    if (!otherIds) return ''
    let otherIdStr = ''
    if (otherIds.tokara) {
      if (Array.isArray(otherIds.tokara)) {
        if (otherIds.tokara.length > 0) {
          otherIdStr += 'tokara: '
          otherIds.tokara.forEach((tokId) => {
            const tokCust = DataMgr.getTokaraCustomers().get(parseInt(tokId))
            if (tokCust) {
              otherIdStr += tokCust.name + ' (' + tokCust.id.toString() + ') '
            }
          })
        }
      } else {
        const tokCust = DataMgr.getTokaraCustomers().get(parseInt(otherIds.tokara))
        if (tokCust) {
          otherIdStr += 'tokara: ' + tokCust.name + ' (' + tokCust.id.toString() + ')'
        }
      }
    }
    if (otherIds.alldayrtk) {
      if (otherIdStr) otherIdStr += ', '
      otherIdStr += 'alldayrtk: ' + otherIds.alldayrtk
    }
    return otherIdStr
  }
}
